<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz, type GridOptions } from 'ag-grid-enterprise';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import type { CrewBriefingTemplate } from '@/api/client/models/index.ts';
import CrewBriefingTemplateActions from '@/charter/components/CrewBriefingTemplateActions.vue';
import CrewBriefingTemplateUploadDialog from '@/charter/components/CrewBriefingTemplateUploadDialog.vue';
import { useCharterCrewBriefingTemplatesStore } from '@/charter/stores/charter-crew-briefing-templates-store.ts';
import { formatDateTime } from '@/util/date-formatting.ts';
import { getApiErrorMessages } from '@/util/error-utils.ts';
import { formatFileSize } from '@/util/file-formatting.ts';

const templatesStore = useCharterCrewBriefingTemplatesStore();
const confirm = useConfirm();
const toast = useToast();
const uploadDialogVisible = ref(false);
const gridTheme = themeQuartz.withParams({});

function showErrors(error: unknown, fallbackMessage: string): void {
  getApiErrorMessages(error, fallbackMessage).forEach(detail => {
    toast.add({
      severity: 'error',
      summary: 'An error occurred',
      detail,
      life: 5000,
    });
  });
}

async function refreshData(): Promise<void> {
  try {
    await templatesStore.fetchTemplates();
  } catch (error) {
    showErrors(error, 'Crew briefing templates could not be loaded.');
  }
}

async function downloadTemplate(fileName: string): Promise<void> {
  try {
    await templatesStore.downloadTemplate(fileName);
  } catch (error) {
    showErrors(error, 'Crew briefing template could not be downloaded.');
  }
}

async function deleteTemplate(fileName: string): Promise<void> {
  try {
    await templatesStore.deleteTemplate(fileName);
    toast.add({
      severity: 'success',
      summary: 'Template deleted',
      detail: `${fileName} was deleted successfully.`,
      life: 3000,
    });
  } catch (error) {
    showErrors(error, 'Crew briefing template could not be deleted.');
  }
}

function confirmDelete(fileName: string): void {
  confirm.require({
    header: 'Delete template',
    message: `Are you sure you want to delete ${fileName}?`,
    accept: () => void deleteTemplate(fileName),
  });
}

const gridOptions: GridOptions<CrewBriefingTemplate> = {
  pagination: true,
  paginationPageSize: 10,
  overlayNoRowsTemplate: 'No crew briefing templates found.',
  autoSizeStrategy: {
    type: 'fitGridWidth',
    defaultMinWidth: 120,
  },
  defaultColDef: {
    filter: true,
    resizable: false,
    sortable: true,
  },
  columnDefs: [
    { field: 'fileName', headerName: 'File name', flex: 2, minWidth: 220 },
    {
      field: 'size',
      headerName: 'Size',
      valueFormatter: params => formatFileSize(params.value),
    },
    {
      field: 'lastModified',
      headerName: 'Last modified',
      minWidth: 180,
      valueFormatter: params => formatDateTime(params.value),
    },
    {
      headerName: 'Actions',
      cellRenderer: CrewBriefingTemplateActions,
      cellRendererParams: {
        onDownload: (fileName: string) => void downloadTemplate(fileName),
        onDelete: confirmDelete,
      },
      filter: false,
      sortable: false,
      pinned: 'right',
      width: 120,
      suppressSizeToFit: true,
    },
  ],
};

onMounted(() => void refreshData());
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h2>Crew briefing templates</h2>
      <div class="flex gap-2">
        <p-button
          label="Upload"
          icon="pi pi-upload"
          severity="primary"
          outlined
          @click="uploadDialogVisible = true"
        />
        <p-button
          label="Refresh"
          icon="pi pi-refresh"
          severity="secondary"
          outlined
          :loading="templatesStore.loading"
          @click="refreshData"
        />
      </div>
    </div>

    <p-message
      v-if="templatesStore.error"
      severity="error"
      :closable="true"
      @close="templatesStore.clearError"
    >
      {{ templatesStore.error }}
    </p-message>

    <AgGridVue
      :rowData="templatesStore.templates"
      :loading="templatesStore.loading"
      :gridOptions="gridOptions"
      :theme="gridTheme"
      class="ag-grid-container"
    />

    <CrewBriefingTemplateUploadDialog v-model:visible="uploadDialogVisible" />
  </div>
</template>

<style scoped lang="scss">
.ag-grid-container {
  width: 100%;
  height: 400px;
}

h2 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}
</style>
